import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaArrowRight, FaBuilding } from "react-icons/fa";

import { industries } from "../data/industries";

/**
 * Third distinct pattern on the home page. Services is a centred light grid,
 * Technology a dark glass band; this is an asymmetric split — a sticky intro
 * column beside the cards — with a peach flood on hover.
 *
 * The flood keeps the vivid brand peach and switches text to navy rather
 * than white: white on #ff9066 is only 2.8:1, navy on it is 8:1.
 */
function IndustriesSection() {
  return (
    <section className="ind">
      <style>{`
        .ind {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;

          position: relative;
          overflow: hidden;
          padding: clamp(60px, 8vw, 104px) 0;
          background: linear-gradient(to bottom, #ffffff 0%, #fff8f5 60%, #ffffff 100%);
        }
        .ind__blob {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(90px); opacity: .55;
          width: 28rem; height: 28rem; top: 12%; left: -12rem;
          background: radial-gradient(circle, #ffd9c6, transparent 70%);
        }

        /* --- Intro column --- */
        .ind__intro { position: relative; }
        @media (min-width: 992px) {
          /* Rides alongside the cards while they scroll past */
          .ind__intro { position: sticky; top: 100px; }
        }
        .ind__badge {
          display: inline-flex; align-items: center; gap: 9px;
          background: var(--peach-tint); color: var(--peach-ink);
          padding: 9px 22px; border-radius: 999px;
          font-weight: 700; font-size: .82rem; letter-spacing: .08em;
        }
        .ind__title {
          margin-top: 22px; color: #0f172a; font-weight: 800;
          font-size: clamp(1.8rem, 3.4vw, 2.9rem);
          line-height: 1.15; letter-spacing: -.02em;
        }
        .ind__title em {
          font-style: normal;
          background: linear-gradient(105deg, var(--peach-deep), var(--peach) 60%, var(--peach-soft));
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .ind__lead {
          margin-top: 16px; color: #64748b;
          font-size: clamp(.96rem, 1.6vw, 1.05rem); line-height: 1.85;
        }
        .ind__rule {
          width: 68px; height: 4px; border-radius: 999px; margin: 24px 0;
          background: linear-gradient(90deg, var(--peach-deep), var(--peach-soft));
        }
        @media (max-width: 991.98px) {
          .ind__rule { margin-left: auto; margin-right: auto; }
        }

        /* --- Cards --- */
        .ind-card {
          position: relative; overflow: hidden;
          display: flex; flex-direction: column; height: 100%;
          padding: 26px 24px;
          border-radius: 20px;
          background: #fff;
          border: 1px solid #f0e4dd;
          box-shadow: 0 6px 22px rgba(194,65,12,.07);
          text-decoration: none;
          transition: transform .28s ease, box-shadow .28s ease;
        }
        /* Corner seed that scales up to flood the whole card */
        .ind-card::before {
          content: "";
          position: absolute; top: -46px; right: -46px;
          width: 108px; height: 108px; border-radius: 50%;
          background: linear-gradient(135deg, var(--peach), var(--peach-soft));
          opacity: .16;
          transition: transform .5s cubic-bezier(.22,1,.36,1), opacity .34s ease;
        }
        .ind-card > * { position: relative; z-index: 1; }
        .ind-card:focus-visible {
          outline: 3px solid var(--peach-deep); outline-offset: 3px;
        }
        .ind-card__icon {
          width: 58px; height: 58px; border-radius: 50%;
          display: grid; place-items: center; margin-bottom: 18px;
          background: var(--peach-tint); color: var(--peach-deep);
          transition: background .3s ease, color .3s ease, transform .3s ease;
        }
        .ind-card__title {
          color: #0f172a; font-weight: 700; font-size: 1.12rem;
          margin-bottom: 10px; transition: color .3s ease;
        }
        .ind-card__text {
          color: #64748b; font-size: .93rem; line-height: 1.75;
          margin-bottom: 18px; transition: color .3s ease;
        }
        .ind-card__more {
          margin-top: auto;
          display: inline-flex; align-items: center; gap: 8px;
          color: var(--peach-ink); font-weight: 700; font-size: .9rem;
          transition: color .3s ease;
        }
        .ind-card__more svg { transition: transform .28s ease; }

        /* A mouse hovers; a finger can't. On touch, lib/touchMotion.js adds
           .is-live as the card scrolls through the middle of the screen, so
           the peach flood still plays — one card at a time. */
        .ind-card:hover,
        .ind-card.is-live {
          transform: translateY(-7px);
          box-shadow: 0 24px 48px rgba(194,65,12,.24);
        }
        .ind-card:hover::before,
        .ind-card.is-live::before { transform: scale(11); opacity: 1; }
        /* Navy on peach = 8:1; white would have been 2.8:1 */
        .ind-card:hover .ind-card__title,
        .ind-card:hover .ind-card__text,
        .ind-card:hover .ind-card__more,
        .ind-card.is-live .ind-card__title,
        .ind-card.is-live .ind-card__text,
        .ind-card.is-live .ind-card__more { color: #0f172a; }
        .ind-card:hover .ind-card__icon,
        .ind-card.is-live .ind-card__icon {
          background: #fff; color: var(--peach-deep); transform: scale(1.06);
        }
        .ind-card:hover .ind-card__more svg,
        .ind-card.is-live .ind-card__more svg { transform: translateX(5px); }

        .ind__cta {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          border: none; border-radius: 14px; color: #0f172a; font-weight: 700;
          box-shadow: 0 12px 30px rgba(255,107,53,.38);
          transition: transform .22s ease, box-shadow .22s ease;
        }
        .ind__cta:hover, .ind__cta:focus-visible {
          background: linear-gradient(135deg, #ff7d4d, #ffa07d);
          transform: translateY(-3px);
          box-shadow: 0 18px 40px rgba(255,107,53,.5);
        }
        @media (max-width: 575.98px) { .ind__cta { width: 100%; } }

        @media (prefers-reduced-motion: reduce) {
          .ind-card, .ind-card::before, .ind-card__icon,
          .ind-card__more svg, .ind__cta { transition: none; }
          .ind-card:hover, .ind__cta:hover { transform: none; }
          .ind-card:hover::before { transform: scale(11); }
        }
      `}</style>

      <span className="ind__blob" />

      <Container style={{ position: "relative", zIndex: 1 }}>
        <Row className="g-4 g-lg-5">
          {/* Intro — sticky beside the cards from lg up */}
          <Col lg={4}>
            <div
              className="ind__intro text-center text-lg-start"
              data-aos="fade-right"
            >
              <span className="ind__badge">
                <FaBuilding />
                INDUSTRIES WE SERVE
              </span>

              {/* theme.css hides <br> inside section headings below 576px,
                  so the explicit space is what keeps "Expertise" and "That"
                  from running together once the break disappears. */}
              <h2 className="ind__title">
                Industry Expertise
                <br />{" "}
                <em>That Delivers Results</em>
              </h2>

              <span className="ind__rule d-block" />

              <p className="ind__lead">
                Combining technology expertise, programme delivery, and digital
                transformation capabilities across multiple industries and
                business sectors.
              </p>

              <Button
                as={Link}
                to="/industries"
                size="lg"
                className="ind__cta mt-3 px-4 py-3 d-inline-flex align-items-center justify-content-center gap-2"
              >
                Explore Industries
                <FaArrowRight />
              </Button>
            </div>
          </Col>

          {/* Cards */}
          <Col lg={8}>
            <Row className="g-4">
              {industries.map((industry, i) => {
                const { Icon } = industry;
                return (
                  <Col
                    md={6}
                    key={industry.slug}
                    data-aos="fade-up"
                    data-aos-delay={(i % 2) * 90}
                  >
                    <Link
                      to={`/industries/${industry.slug}`}
                      className="ind-card"
                      data-live
                    >
                      <span className="ind-card__icon">
                        <Icon size={24} />
                      </span>

                      <h3 className="ind-card__title">{industry.title}</h3>
                      <p className="ind-card__text">{industry.description}</p>

                      <span className="ind-card__more">
                        Learn more <FaArrowRight />
                      </span>
                    </Link>
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default IndustriesSection;
